import { memo, useEffect, useState } from 'react'
import { Clapperboard, Upload, X } from 'lucide-react'
import type { CasparTransitionType, SlideshowTransition, TransitionDirection, TransitionEasing } from '../../lib/infoserviceSlideshowApi.js'
import { CASPAR_TRANSITION_OPTIONS, formatSeconds, parseSecondsInput } from '../../lib/slideshowSettingsForm.js'
import {
  DEFAULT_SMOOTH_EASING,
  EASING_CURVE_OPTIONS,
  EASING_PROFILE_OPTIONS,
  easingOf,
  easingParts,
  easingPath,
  type EasingCurve,
} from '../../lib/transitionEasing.js'
import { TransitionPreview } from './TransitionPreview.js'

interface TransitionEditorProps {
  title: string
  value: SlideshowTransition
  /** Fichier du stinger tel qu'il est enregistré (choisi à part, tout de suite). */
  savedStinger: Extract<SlideshowTransition, { kind: 'STINGER' }> | null
  busy: boolean
  onChange: (next: SlideshowTransition) => void
  onPickStinger: () => void
  onRemoveStinger: () => void
}

const CASPAR_DEFAULT: SlideshowTransition = { kind: 'CASPAR', type: 'MIX', durationMs: 500, direction: 'LEFT', easing: DEFAULT_SMOOTH_EASING }

const CURVE_BOX = 44

/** Courbe de Bézier du mouvement : temps en abscisse, progression en ordonnée. */
const EasingGraph = memo(function EasingGraph({ easing }: { easing: TransitionEasing }) {
  return (
    <svg className="easing-graph" viewBox={`-4 -4 ${CURVE_BOX + 8} ${CURVE_BOX + 8}`} width={CURVE_BOX + 8} height={CURVE_BOX + 8} aria-hidden="true">
      <rect className="easing-graph__frame" x={0} y={0} width={CURVE_BOX} height={CURVE_BOX} />
      <path className="easing-graph__curve" d={easingPath(easing, CURVE_BOX)} />
    </svg>
  )
})

/** Intensité de la courbe, puis profil (ease in / out / les deux). */
const EasingField = memo(function EasingField({ easing, onChange }: { easing: TransitionEasing; onChange: (next: TransitionEasing) => void }) {
  const { curve, profile } = easingParts(easing)
  const linear = curve === 'LINEAR'
  return (
    <div className="slideshow-field transition-easing">
      <span className="tp-field-label">Courbe</span>
      <div className="transition-easing__row">
        <EasingGraph easing={easing} />
        <div className="transition-easing__controls">
          <select className="tp-input" aria-label="Intensité de la courbe" value={curve}
            title={EASING_CURVE_OPTIONS.find(option => option.value === curve)?.hint}
            onChange={event => onChange(easingOf(event.target.value as EasingCurve, profile))}>
            {EASING_CURVE_OPTIONS.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
          </select>
          <div className="tp-segmented" role="group" aria-label="Profil de la courbe">
            {EASING_PROFILE_OPTIONS.map(option => (
              <button key={option.value} type="button" title={option.hint} disabled={linear}
                className={`tp-segment${!linear && profile === option.value ? ' is-active' : ''}`}
                aria-pressed={!linear && profile === option.value}
                onClick={() => onChange(easingOf(curve, option.value))}>{option.label}</button>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
})

/** Saisie en secondes qui ne réécrit pas le texte pendant la frappe. */
const SecondsField = memo(function SecondsField({ label, valueMs, minMs, maxMs, disabled, onCommit }: {
  label: string; valueMs: number; minMs: number; maxMs: number; disabled?: boolean; onCommit: (ms: number) => void
}) {
  const [text, setText] = useState(() => String(valueMs / 1000).replace('.', ','))
  useEffect(() => setText(String(valueMs / 1000).replace('.', ',')), [valueMs])
  const parsed = parseSecondsInput(text)
  const invalid = parsed === null || parsed < minMs || parsed > maxMs
  return (
    <label className="slideshow-field">
      <span className="tp-field-label">{label}</span>
      <span className="slideshow-field__unit">
        <input
          className={`tp-input${invalid ? ' is-invalid' : ''}`}
          inputMode="decimal"
          value={text}
          disabled={disabled}
          aria-invalid={invalid}
          onChange={event => {
            setText(event.target.value)
            const ms = parseSecondsInput(event.target.value)
            if (ms !== null && ms >= minMs && ms <= maxMs) onCommit(ms)
          }}
        />
        <span aria-hidden="true">s</span>
      </span>
    </label>
  )
})

export const TransitionEditor = memo(function TransitionEditor({
  title, value, savedStinger, busy, onChange, onPickStinger, onRemoveStinger,
}: TransitionEditorProps) {
  const isStinger = value.kind === 'STINGER'
  const directional = value.kind === 'CASPAR' && ['WIPE', 'PUSH', 'SLIDE'].includes(value.type)
  // Carte survolée ou au focus : elle s'anime aussi, en plus de la carte choisie.
  const [previewing, setPreviewing] = useState<CasparTransitionType | null>(null)

  return (
    <section className="slideshow-transition" aria-label={title}>
      <header className="slideshow-transition__header">
        <h4>{title}</h4>
        <div className="tp-segmented" role="group" aria-label={`${title} : type`}>
          <button type="button" className={`tp-segment${!isStinger ? ' is-active' : ''}`} aria-pressed={!isStinger}
            onClick={() => !isStinger || onChange(CASPAR_DEFAULT)}>Transition CasparCG</button>
          <button type="button" className={`tp-segment${isStinger ? ' is-active' : ''}`} aria-pressed={isStinger}
            disabled={!savedStinger}
            title={savedStinger ? undefined : 'Choisissez d’abord le fichier du stinger'}
            onClick={() => savedStinger && onChange(savedStinger)}>Stinger</button>
        </div>
      </header>

      {value.kind === 'CASPAR' && (
        <div className="slideshow-transition__body">
          <div className="slideshow-field slideshow-transition__types">
            <span className="tp-field-label">Type</span>
            <div className="transition-gallery" role="group" aria-label="Type de transition">
              {CASPAR_TRANSITION_OPTIONS.map(option => {
                const active = value.type === option.value
                // Chaque vignette reprend la durée et le sens réglés : elle montre
                // ce que donnerait ce type, pas un exemple générique.
                const sample: SlideshowTransition = {
                  ...value,
                  type:       option.value,
                  durationMs: option.value === 'CUT' ? 0 : (value.durationMs || 500),
                }
                return (
                  <button key={option.value} type="button"
                    className={`transition-card${active ? ' is-active' : ''}`}
                    aria-pressed={active}
                    onPointerEnter={() => setPreviewing(option.value)}
                    onPointerLeave={() => setPreviewing(current => (current === option.value ? null : current))}
                    onFocus={() => setPreviewing(option.value)}
                    onBlur={() => setPreviewing(current => (current === option.value ? null : current))}
                    onClick={() => onChange(sample)}>
                    <TransitionPreview transition={sample} playing={active || previewing === option.value} />
                    <span className="transition-card__label">{option.label}</span>
                  </button>
                )
              })}
            </div>
          </div>
          {value.type !== 'CUT' && (
            <SecondsField label="Durée" valueMs={value.durationMs} minMs={40} maxMs={5000}
              onCommit={durationMs => onChange({ ...value, durationMs })} />
          )}
          {value.type !== 'CUT' && (
            <EasingField easing={value.easing ?? 'LINEAR'} onChange={easing => onChange({ ...value, easing })} />
          )}
          {directional && (
            <label className="slideshow-field">
              <span className="tp-field-label">Sens</span>
              <select className="tp-input" value={value.direction}
                onChange={event => onChange({ ...value, direction: event.target.value as TransitionDirection })}>
                {/* Le mot CasparCG désigne le sens du MOUVEMENT (vérifié à l'image sur 2.5) :
                    LEFT = la nouvelle image entre par la droite et file vers la gauche. */}
                <option value="LEFT">Droite → gauche</option>
                <option value="RIGHT">Gauche → droite</option>
              </select>
            </label>
          )}
        </div>
      )}

      {!isStinger && (
        <div className="slideshow-transition__stinger-file is-quiet">
          <Clapperboard size={14} aria-hidden="true" />
          <span>{savedStinger ? `Stinger prêt : ${savedStinger.filePath.split('/').pop()} · ${formatSeconds(savedStinger.durationMs)}` : 'Aucun stinger importé'}</span>
          <button type="button" className="tp-button" disabled={busy} onClick={onPickStinger}>
            <Upload size={14} aria-hidden="true" /> {savedStinger ? 'Remplacer' : 'Choisir un fichier'}
          </button>
        </div>
      )}

      {value.kind === 'STINGER' && (
        <div className="slideshow-transition__body is-stinger">
          <div className="slideshow-transition__stinger-file">
            <Clapperboard size={14} aria-hidden="true" />
            <span className="slideshow-mono">{value.filePath.split('/').pop()}</span>
            <span className="slideshow-muted">{formatSeconds(value.durationMs)}</span>
            <button type="button" className="tp-button" disabled={busy} onClick={onPickStinger}>Remplacer</button>
            <button type="button" className="tp-icon-button" disabled={busy} onClick={onRemoveStinger} aria-label="Retirer le stinger">
              <X size={14} aria-hidden="true" />
            </button>
          </div>
          <div className="slideshow-transition__stinger-preview">
            <span className="transition-card is-active is-static">
              <TransitionPreview transition={value} playing />
            </span>
            <p className="slideshow-hint">Schéma : la bande figure votre stinger, à sa vraie durée et avec son point de coupe.</p>
          </div>
          <SecondsField label="Point de coupe" valueMs={value.cutMs} minMs={0} maxMs={value.durationMs}
            onCommit={cutMs => onChange({ ...value, cutMs })} />
          <div className="slideshow-transition__cut" aria-hidden="true">
            <span className="slideshow-transition__cut-bar" />
            <span className="slideshow-transition__cut-mark" style={{ left: `${(value.cutMs / value.durationMs) * 100}%` }}>
              {formatSeconds(value.cutMs)}
            </span>
          </div>
          <p className="slideshow-hint">L’annonce suivante apparaît sous le stinger au point de coupe, quand il couvre tout l’écran.</p>
        </div>
      )}
    </section>
  )
})
